import {
  getPasswordPolicyError,
  getPasswordPolicyMessage,
  normalizeAuthErrorCode,
} from '../../utils/auth-security-policy.mjs'

const PASSWORD_LOGIN_TYPES = new Set(['django', 'django-prosecution'])

const ERROR_CODE_CONFIG = {
  '1001190': {
    field: 'form',
    fallback: '비밀번호 로그인 사용자만 변경할 수 있습니다.',
  },
  '1001191': {
    field: 'currentPassword',
    fallback: '현재 비밀번호를 다시 확인해 주세요.',
  },
  '1001192': {
    field: 'newPasswordConfirm',
    fallback: '새 비밀번호 확인이 일치하지 않습니다.',
  },
  '1001193': {
    field: 'newPassword',
    fallback: '현재 비밀번호와 다른 비밀번호를 입력해 주세요.',
  },
}

function createEmptyErrors() {
  return {
    form: '',
    currentPassword: '',
    newPassword: '',
    newPasswordConfirm: '',
  }
}

function getErrorData(errorOrData = {}) {
  return errorOrData && errorOrData.response && errorOrData.response.data
    ? errorOrData.response.data
    : errorOrData || {}
}

function getFirstFieldError(value) {
  const firstValue = Array.isArray(value) ? value[0] : value
  if (firstValue && typeof firstValue === 'object') {
    return firstValue.message || ''
  }
  return firstValue ? String(firstValue) : ''
}

export function canChangeAuthenticatedPassword(userType = '') {
  return PASSWORD_LOGIN_TYPES.has(userType)
}

export function validateAuthenticatedPasswordChange(form = {}) {
  const errors = createEmptyErrors()
  const currentPassword = form.currentPassword || ''
  const newPassword = form.newPassword || ''
  const newPasswordConfirm = form.newPasswordConfirm || ''

  if (!currentPassword) {
    errors.currentPassword = '현재 비밀번호를 입력해 주세요.'
  } else if (Array.from(currentPassword).length > 128) {
    errors.currentPassword = '현재 비밀번호는 128자 이하로 입력해 주세요.'
  }

  if (!newPassword) {
    errors.newPassword = '새 비밀번호를 입력해 주세요.'
  } else {
    errors.newPassword = getPasswordPolicyError(newPassword)
  }

  if (!newPasswordConfirm) {
    errors.newPasswordConfirm = '새 비밀번호 확인을 입력해 주세요.'
  } else if (newPassword !== newPasswordConfirm) {
    errors.newPasswordConfirm = '새 비밀번호 확인이 일치하지 않습니다.'
  }

  return errors
}

export function createPasswordChangePayload(form = {}) {
  return {
    current_password: form.currentPassword,
    new_password1: form.newPassword,
    new_password2: form.newPasswordConfirm,
  }
}

export function mapAuthenticatedPasswordChangeError(errorOrData = {}) {
  const data = getErrorData(errorOrData)
  const errors = createEmptyErrors()
  const code = normalizeAuthErrorCode(data)

  if (code === '1001184') {
    errors.newPassword = getPasswordPolicyMessage(data)
    return errors
  }

  const codeConfig = ERROR_CODE_CONFIG[code]
  if (codeConfig) {
    errors[codeConfig.field] = data.err_msg || codeConfig.fallback
    return errors
  }

  errors.currentPassword = getFirstFieldError(data.current_password)
  errors.newPassword = getFirstFieldError(data.new_password1)
  errors.newPasswordConfirm = getFirstFieldError(data.new_password2)

  if (!errors.currentPassword && !errors.newPassword && !errors.newPasswordConfirm) {
    errors.form = getFirstFieldError(data.err_msg) || '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.'
  }

  return errors
}

export function createAuthenticatedPasswordChangeSubmitter(changePassword) {
  let submitting = false

  return {
    isSubmitting() {
      return submitting
    },
    async submit(form) {
      if (submitting) {
        return { status: 'ignored' }
      }

      const errors = validateAuthenticatedPasswordChange(form)
      if (Object.values(errors).some(Boolean)) {
        return { status: 'validation-error', errors }
      }

      submitting = true
      try {
        await changePassword(createPasswordChangePayload(form))
        return { status: 'success' }
      } catch (error) {
        return {
          status: 'request-error',
          errors: mapAuthenticatedPasswordChangeError(error),
        }
      } finally {
        submitting = false
      }
    },
  }
}
